import { swivuRecentRecordSortFn } from "@/modern/utils";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { ENTITY_TYPES, VIEW_TYPES } from "@/other/constants/OtherKeys";
import { SWIVU_STANDARD_ICONS } from "@/other/constants/IconKeys.jsx";
import type { Client } from "@/bryntum-lib/models/Client";
import { EntityFinderHighlight } from "@/modern/components/entity-finder/components/EntityFinderHighlight";
import { EntityFinderItemRow, entityFinderTintText } from "@/modern/components/entity-finder/components/EntityFinderItemRow";
import { renderEntityFinderConfigIcon } from "@/modern/components/entity-finder/components/renderEntityFinderConfigIcon";
import {
  canCreate,
  estimateDefaultRowHeight,
  openEntityEditor,
} from "@/modern/components/entity-finder/constants/config/shared";
import type { EntityFinderConfigEntry } from "@/modern/components/entity-finder/types";
import {
  defaultEntityListSection,
  defaultMatchesScope,
  matchesSearch,
  passesArchiveFilter,
  searchModeActive,
} from "@/modern/components/entity-finder/utils";

function mapClient(client: Client) {
  return {
    id: `client-${client.id}`,
    type: "client" as const,
    label: client.name || "N/A",
    subtitle: client.email || undefined,
    record: client,
  };
}

export const clientConfig: EntityFinderConfigEntry<"client"> = {
  type: "client",
  label: "Client",
  plural: "Clients",
  icon: <i className={SWIVU_STANDARD_ICONS[VIEW_TYPES.clients]} aria-hidden />,
  tint: "bg-chart-2/15 text-chart-2",
  listOrder: 30,
  showInFilter: true,
  isActive: searchModeActive,
  matchesScope: (ctx) => defaultMatchesScope(ctx, "client"),
  getData: (ctx) => {
    const searchLower = ctx.debouncedQuery.toLowerCase();
    const records = swivuRecentRecordSortFn(
      ctx.project.clientsStore.query(
        (client: Client) =>
          passesArchiveFilter(client.archived, ctx.isShowArchive) &&
          !client.isLinked &&
          (matchesSearch(client.name, searchLower) || matchesSearch(client.email, searchLower)),
        true
      ) as unknown as Client[],
      { currentBoardUserId: ctx.currentBoardUserId }
    );
    return records.map(mapClient);
  },
  getRecentItem: (ctx) => {
    const records = swivuRecentRecordSortFn(
      ctx.project.clientsStore.query(
        (c: Client) => passesArchiveFilter(c.archived, ctx.isShowArchive) && !c.isLinked,
        true
      ) as unknown as Client[],
      { currentBoardUserId: ctx.currentBoardUserId }
    ).slice(0, 1);
    return records[0] ? mapClient(records[0]) : null;
  },
  buildListSection: (items, _ctx) => defaultEntityListSection(clientConfig, items),
  estimateRowHeight: estimateDefaultRowHeight,
  itemRenderer: ({ item, query, entry }) => (
    <EntityFinderItemRow
      icon={renderEntityFinderConfigIcon(entry.icon, 14, "text-sm")}
      iconClassName={entityFinderTintText(clientConfig.tint)}
      title={<EntityFinderHighlight text={item.label} query={query} />}
      subtitle={
        item.subtitle ? (
          <EntityFinderHighlight text={item.subtitle} query={query} tone="muted" />
        ) : undefined
      }
    />
  ),
  onSelect: (item, ctx) => {
    ctx.entityManager.openEntityManager({
      entityType: ENTITY_TYPES.CLIENT,
      entityId: (item.record as Client).id,
    });
  },
  createOnUnmatched: {
    label: "Create new client",
    fn: (query, ctx) => {
      const trimmed = query.trim();
      if (!trimmed) return;
      const newRecord = (
        ctx.project.clientsStore as { createRecord: (data?: object) => unknown }
      ).createRecord({ name: trimmed });
      openEntityEditor(ctx, EDITOR_KEYS.clientEditorV2, newRecord, { client: newRecord });
    },
    isEnabled: canCreate,
  },
};
